import {
  Box,
  Button,
  Center,
  Image,
  Skeleton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import ResponsiveLineChart from "components/company/line-chart/line-chart";
import ProfileCard, {
  ProfileCardHeader,
} from "components/profile/profile-card";
import { useRequestUpdate } from "components/profile/update-modals/request-update-modal";
import usePermission from "hooks/use-permission";
// import useSendEnterpriseEmail from "hooks/use-send-enterprise-email";
import { useRouter } from "next/router";
import useUser from "hooks/use-user";
import { abbreviateCurrency, formatNumber } from "utils/number";
import type { EmployeeRevenueChartData } from "types/api/company/details/overview";
import type { ChartSeries } from "../line-chart/types";

const formatChartRow = (
  row: EmployeeRevenueChartData[keyof EmployeeRevenueChartData]
) => {
  if (!row) {
    return [];
  }

  return Object.entries(row).map(([year, value]) => ({
    x: new Date(`${year}-15`),
    y: value,
  }));
};

const formatChartData = (
  chartData: EmployeeRevenueChartData
): ChartSeries[] => [
  {
    name: "Revenue",
    formatter: abbreviateCurrency,
    color: "var(--chakra-colors-blue-500)",
    data: formatChartRow(chartData.revenues),
  },
  {
    name: "Employees",
    formatter: formatNumber,
    color: "var(--chakra-colors-green-500)",
    data: formatChartRow(chartData.employees),
  },
];

interface EmployeesRevenueCardProps {
  isLoaded: boolean;
  chartData?: EmployeeRevenueChartData;
  hasChartData?: boolean;
  isInternational?: boolean;
}

const EmployeesRevenueCard = ({
  isLoaded,
  chartData,
  hasChartData = false,
  isInternational = false,
}: EmployeesRevenueCardProps) => {
  const user = useUser();
  const router = useRouter();
  const isPublic = !user?.isLoggedIn || false;
  const isFree = user?.access?.group === "free";

  const redirectToAcct = () => {
    router.push({
        pathname: `/account`,
    });
  };

  // const sendEnterpriseEmail = useSendEnterpriseEmail();
  
  const { openRequestUpdateModal } = useRequestUpdate();
  const requestUpdate = () => {
    openRequestUpdateModal({
      options: ["overview"],
      message: "Employees vs. Revenue chart",
    });
  };

  const hasChart = usePermission("companyOverviewChartData");

  const getCardBody = () => {
    if (!isLoaded) {
      return <Skeleton h="300px" />;
    }

    if (!hasChartData) {
      return (
        <Center py={4} h="300px" flexDirection="column">
          <Text maxW="32rem">We are adding new insights on a daily basis!</Text>
          <Tooltip
            isDisabled={!isInternational}
            label="Requesting updates for international companies is currently not available"
            shouldWrapChildren
          >
            <Button
              colorScheme="blue"
              mt={4}
              onClick={requestUpdate}
              isDisabled={isInternational}
              data-cy="click-chart-update-button"
            >
              Request Update
            </Button>
          </Tooltip>
        </Center>
      );
    }

    if (isPublic) {
      return (
        <Center h="300px">
          <Image
            src="/images/cagr-placeholder.png"
            alt="Employees vs. Revenue Chart"
            h="300px"
            style={{ filter: "grayscale(100%) blur(2px)" }}
          />
        </Center>
      );
    }

    if (!hasChart) {
      return (
        <Center h="300px" position="relative">
          <Image
            src="/images/emp-rev-placeholder.png"
            alt="Employees vs. Revenue Chart"
            style={{ filter: "grayscale(100%) blur(2px)" }}
            h="300px"
          />
          <Button
            position="absolute"
            onClick={redirectToAcct}
            colorScheme="blue"
            data-cy="profile-graph-contact-enterprise"
          >
            Enterprise Feature
          </Button>
        </Center>
      );
    }

    return (
      <Box
        h="300px"
        position="relative"
        {...(isFree
          ? {
              sx: {
                filter: "blur(13px)",
              },
            }
          : {})}
      >
        <ResponsiveLineChart series={formatChartData(chartData)} hasMonths />
      </Box>
    );
  };

  return (
    <ProfileCard>
      <ProfileCardHeader>Employees vs. Revenue</ProfileCardHeader>
      {getCardBody()}
    </ProfileCard>
  );
};

export default EmployeesRevenueCard;
